import { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabaseClient';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import InfoModal from '@/components/ui/InfoModal';
import { useVideoBySlug } from '@/hooks/useVideos';
import VideoLink from '@/components/ui/VideoLink';

interface UserSettings {
  vapi_assistant_id?: string | null;
}

const DEFAULT_PRIMARY_COLOR = '#3A5AFF';
const DEFAULT_ACCENT_COLOR = '#F2F4F7';
const DEFAULT_MAIN_ICON_URL = '';
const DEFAULT_CONNECTING_ICON_URL = '';
const DEFAULT_LISTENING_ICON_URL = '';
const DEFAULT_WIDGET_TYPE = 'block' as 'block' | 'floating';
const DEFAULT_WIDGET_SIZE = 100; 

export default function WebWidgetTab() {
  const webWidgetOverviewVideo = useVideoBySlug('web-call-widget-overview');
  const { data: settings } = useQuery<UserSettings | null>({
    queryKey: ['user-settings'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/user-settings`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) {
        console.error('Failed to fetch user settings');
        return null;
      }
      return res.json();
    },
    refetchOnWindowFocus: false,
  });

  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY_COLOR);
  const [accentColor, setAccentColor] = useState(DEFAULT_ACCENT_COLOR);
  const [mainIconUrl, setMainIconUrl] = useState(DEFAULT_MAIN_ICON_URL);
  const [connectingIconUrl, setConnectingIconUrl] = useState(DEFAULT_CONNECTING_ICON_URL);
  const [listeningIconUrl, setListeningIconUrl] = useState(DEFAULT_LISTENING_ICON_URL);
  const [widgetType, setWidgetType] = useState<'block' | 'floating'>(DEFAULT_WIDGET_TYPE);
  const [widgetSize, setWidgetSize] = useState(DEFAULT_WIDGET_SIZE);

  const assistantId = settings?.vapi_assistant_id || 'YOUR_ASSISTANT_ID_FALLBACK'; 

  const snippet = useMemo(() => {
    return generateSnippet(
      primaryColor, 
      accentColor, 
      mainIconUrl, 
      connectingIconUrl, 
      listeningIconUrl, 
      assistantId, 
      widgetType, 
      widgetSize
    );
  }, [primaryColor, accentColor, mainIconUrl, connectingIconUrl, listeningIconUrl, assistantId, widgetType, widgetSize]);

  const copySnippet = () => {
    navigator.clipboard.writeText(snippet)
      .then(() => toast.success('Snippet copied to clipboard!'))
      .catch(() => toast.error('Failed to copy snippet.'));
  };

  const resetToDefaults = useCallback(() => {
    setPrimaryColor(DEFAULT_PRIMARY_COLOR);
    setAccentColor(DEFAULT_ACCENT_COLOR);
    setMainIconUrl(DEFAULT_MAIN_ICON_URL);
    setConnectingIconUrl(DEFAULT_CONNECTING_ICON_URL);
    setListeningIconUrl(DEFAULT_LISTENING_ICON_URL);
    setWidgetType(DEFAULT_WIDGET_TYPE);
    setWidgetSize(DEFAULT_WIDGET_SIZE);
    toast.info('Settings reset to defaults.');
  }, []);

  const previewHtml = useMemo(() => {
    const doc = new DOMParser().parseFromString(snippet, 'text/html');
    const styleTag = doc.querySelector('style')?.outerHTML || '';
    const widgetContainer = doc.querySelector('.ai-widget-outer-container')?.outerHTML || doc.querySelector('.ai-widget-button-container')?.outerHTML || '';
    return { __html: styleTag + widgetContainer };
  }, [snippet]);


  return (
    <div className="space-y-8 p-4 md:p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">Web Call Widget</h1>
          <p className="text-sm text-slate-600 mt-1">
            Customize your AI call widget and embed it on your website with a simple copy-paste.
          </p>
        </div>
        <InfoModal 
          title="Web Call Widget Overview" 
          description={
            <div className="space-y-4">
              <p className="text-sm text-gray-700 mb-4">
                The Web Call Widget gives website visitors access to your AI Employee through their browser. It uses the same AI assistant with all your configured knowledge and settings.
              </p>
              {webWidgetOverviewVideo && <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src={webWidgetOverviewVideo || "https://www.loom.com/embed/8afe38acf0a4441d899f04e7f5213b66?sid=d48e8897-6e6d-4173-8715-9fff8088f8f5"}
                  frameBorder="0"
                  allowFullScreen
                  className="absolute top-0 left-0 w-full h-full rounded-lg"
                />
              </div>}
              {webWidgetOverviewVideo && <VideoLink to='web-call-widget-overview' />}

              <div className="space-y-3">
                <div>
                  <h4 className="font-medium mb-1">Key Features:</h4>
                  <ul className="text-sm space-y-1 text-gray-600">
                    <li>• <strong>Same AI Assistant:</strong> Uses all your configured knowledge, services, and settings</li>
                    <li>• <strong>Customizable Design:</strong> Match your brand colors and styling</li>
                    <li>• <strong>Two Display Types:</strong> Inline block or floating button</li>
                    <li>• <strong>Full Functionality:</strong> Booking appointments, answering questions, calendar access</li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-medium mb-1">Limitations:</h4>
                  <ul className="text-sm space-y-1 text-gray-600">
                    <li>• <strong>Separate System:</strong> Web calls are independent from phone calls</li>
                    <li>• <strong>No Phone Transfer:</strong> Cannot transfer to phone numbers</li>
                    <li>• <strong>Browser-Based:</strong> Requires web browser to function</li>
                  </ul>
                </div>
              </div>
            </div>
          }
          triggerClassName="ml-2"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">        
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            <div className="px-4 py-5 sm:px-6 border-b border-slate-200">
              <h3 className="text-lg leading-6 font-medium text-slate-900">Appearance</h3>
              <p className="mt-1 max-w-2xl text-sm text-slate-500">Customize the look and feel of your call widget.</p>
            </div>
            <div className="px-4 py-5 sm:p-6 space-y-4">
              <div>
                <Label htmlFor="primaryColor" className="text-sm font-medium text-slate-700">Primary Color</Label>
                <Input
                  id="primaryColor"
                  type="color"
                  value={primaryColor}
                  onChange={e => setPrimaryColor(e.target.value)}
                  className="mt-1 w-full"
                />
              </div>
              <div>
                <Label htmlFor="accentColor" className="text-sm font-medium text-slate-700">Accent Color (Pulse Rings)</Label>
                <Input
                  id="accentColor"
                  type="color"
                  value={accentColor}
                  onChange={e => setAccentColor(e.target.value)}
                  className="mt-1 w-full"
                />
              </div>
              <div>
                <Label htmlFor="mainIconUrl" className="text-sm font-medium text-slate-700">Main Icon URL (Optional)</Label>
                <Input
                  id="mainIconUrl"
                  type="text"
                  value={mainIconUrl}
                  onChange={e => setMainIconUrl(e.target.value)}
                  placeholder="https://your-domain.com/icon.png"
                  className="mt-1"
                />
                 <p className="text-xs text-slate-500 mt-1">Replaces the default microphone icon. Recommended: square image.</p>
              </div>
              <div>
                <Label htmlFor="connectingIconUrl" className="text-sm font-medium text-slate-700">Connecting Icon URL (Optional)</Label>
                <Input
                  id="connectingIconUrl"
                  type="text"
                  value={connectingIconUrl}
                  onChange={e => setConnectingIconUrl(e.target.value)}
                  placeholder="https://your-domain.com/connecting.gif"
                  className="mt-1"
                />
                 <p className="text-xs text-slate-500 mt-1">Replaces the default spinner icon. E.g., animated GIF/SVG.</p>
              </div>
              <div>
                <Label htmlFor="listeningIconUrl" className="text-sm font-medium text-slate-700">Listening Icon URL (Optional)</Label>
                <Input
                  id="listeningIconUrl"
                  type="text"
                  value={listeningIconUrl}
                  onChange={e => setListeningIconUrl(e.target.value)}
                  placeholder="https://your-domain.com/listening.png"
                  className="mt-1"
                />
                 <p className="text-xs text-slate-500 mt-1">Replaces the default active call icon. Recommended: square image.</p>
              </div>
            </div>
          </div>

          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            <div className="px-4 py-5 sm:px-6 border-b border-slate-200">
              <h3 className="text-lg leading-6 font-medium text-slate-900">Layout & Size</h3>
              <p className="mt-1 max-w-2xl text-sm text-slate-500">Control how and where the widget appears.</p>
            </div>
            <div className="px-4 py-5 sm:p-6 space-y-6">
              <div className="flex items-center justify-between space-x-2">
                <Label htmlFor="widgetType" className="text-sm font-medium text-slate-700">
                  Display Type
                  <span className="block text-xs text-slate-500 font-normal">
                    {widgetType === 'block' ? 'Inline Block' : 'Floating Button (Bottom Right)'}
                  </span>
                </Label>
                <Switch
                  id="widgetType"
                  checked={widgetType === 'floating'}
                  onCheckedChange={(checked) => setWidgetType(checked ? 'floating' : 'block')}
                />
              </div>
              <div>
                <Label htmlFor="widgetSize" className="text-sm font-medium text-slate-700">
                  Widget Size ({widgetSize}px)
                </Label>
                <Slider
                  id="widgetSize"
                  min={60}
                  max={160}
                  step={4}
                  value={[widgetSize]}
                  onValueChange={(value) => setWidgetSize(value[0])}
                  className="mt-2"
                />
                 <p className="text-xs text-slate-500 mt-1">Adjusts the diameter of the call button.</p>
              </div>
            </div>
          </div>
          
          <Button variant="outline" onClick={resetToDefaults} className="w-full border-slate-300 text-slate-700 hover:bg-slate-50">
            Reset to Defaults
          </Button>
        </div>
        
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            <div className="px-4 py-5 sm:px-6 border-b border-slate-200">
              <h3 className="text-lg leading-6 font-medium text-slate-900">Live Preview</h3>
              <p className="mt-1 max-w-2xl text-sm text-slate-500">See your widget customizations in real-time. Call functionality is disabled in preview.</p>
            </div>
            <div className="px-4 py-5 sm:p-6">
              <div className="min-h-[200px] w-full bg-slate-50 rounded-md flex items-center justify-center p-4 border border-slate-200 relative overflow-hidden">
                <div dangerouslySetInnerHTML={previewHtml} />
                 {widgetType === 'floating' && (
                  <p className="absolute bottom-4 right-4 text-xs text-slate-400 italic">Preview of floating position</p>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            <div className="px-4 py-5 sm:px-6 border-b border-slate-200">
              <h3 className="text-lg leading-6 font-medium text-slate-900">Embed Code</h3>
              <p className="mt-1 max-w-2xl text-sm text-slate-500">Copy and paste this snippet into your website's HTML where you want the widget to appear (e.g., before the closing <code>{'</body>'}</code> tag).</p>
            </div>
            <div className="px-4 py-5 sm:p-6 space-y-3">
              <Textarea
                value={snippet}
                readOnly
                rows={20}
                className="font-mono text-xs bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200 h-80 rounded-md border-slate-300 dark:border-slate-700" 
                aria-label="Embeddable code snippet for the web call widget"
              />
              <Button onClick={copySnippet} className="w-full bg-primary hover:bg-primary/90 text-primary-foreground">
                Copy Snippet
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function hexToRgb(hex: string): string {
  hex = hex.replace('#', '');
  if (hex.length === 3) {
    hex = hex.split('').map(char => char + char).join('');
  }
  const bigint = parseInt(hex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `${r},${g},${b}`;
}

function generateSnippet(
  primaryColor: string,
  accentColor: string,
  mainIconUrl: string,
  connectingIconUrl: string,
  listeningIconUrl: string,
  assistantId: string,
  widgetType: 'block' | 'floating',
  widgetSize: number
): string {  
  const serviceapiKey = 'd916b331-df51-4edc-820c-ad3fd590d2bd'; 

  const safeAssistantId = assistantId || 'ASSISTANT_ID_NOT_CONFIGURED';

  const widgetHtmlId = `ai-widget-call-button-${Math.random().toString(36).substring(7)}`;
  const mainIconId = `ai-widget-icon-main-${Math.random().toString(36).substring(7)}`;
  const connectingIconId = `ai-widget-icon-connecting-${Math.random().toString(36).substring(7)}`;
  const listeningIconId = `ai-widget-icon-listening-${Math.random().toString(36).substring(7)}`;

  const mainIconHtml = mainIconUrl
    ? `<img id="${mainIconId}" src="${mainIconUrl}" alt="AI Call Assistant" class="ai-widget-icon"/>`
    : `<svg id="${mainIconId}" class="ai-widget-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12 14q1.25 0 2.125-.875T15 11V5q0-1.25-.875-2.125T12 2q-1.25 0-2.125.875T9 5v6q0 1.25.875 2.125T12 14Zm-1 7v-3.075q-2.6-.35-4.3-2.312T5 11H3q.175 3.125 2.525 5.313T11 22.075V25h2v-2.925q2.95-.5 5.225-2.687T20.75 11h-2q-.175 2.275-1.875 4.238T13 17.925V21Z"/></svg>`;

  const connectingIconHtml = connectingIconUrl
    ? `<img id="${connectingIconId}" src="${connectingIconUrl}" alt="Connecting" class="ai-widget-icon"/>`
    : `<svg id="${connectingIconId}" class="ai-widget-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path class="ai-widget-spinner" d="M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,19a8,8,0,1,1,8-8A8,8,0,0,1,12,20Z" fill="none" stroke-width="2" stroke-linecap="round"/></svg>`;

  const listeningIconHtml = listeningIconUrl
    ? `<img id="${listeningIconId}" src="${listeningIconUrl}" alt="Listening" class="ai-widget-icon"/>`
    : `<svg id="${listeningIconId}" class="ai-widget-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3zm0 1.5a1.5 1.5 0 0 1 1.5 1.5v6a1.5 1.5 0 0 1-3 0V6A1.5 1.5 0 0 1 12 4.5zM19 11a1 1 0 0 0-1 1a6 6 0 0 1-12 0 1 1 0 1 0-2 0a8 8 0 0 0 7 7.93V21H9a1 1 0 0 0 0 2h6a1 1 0 0 0 0-2h-2v-1.07A8 8 0 0 0 21 12a1 1 0 0 0-1-1z"/></svg>`;

  const containerClass = widgetType === 'floating' ? 'ai-widget-outer-container ai-widget-floating' : 'ai-widget-button-container';

  return `\
<div class="${containerClass}">
  <div id="${widgetHtmlId}" class="ai-widget-idle-animation" role="button" aria-label="Start AI Call">
    <div class="ai-widget-icon-wrapper">
      ${mainIconHtml}
      ${connectingIconHtml}
      ${listeningIconHtml}
    </div>
  </div>
</div>
<style>
  :root {
    --ai-widget-bubble-size: ${widgetSize}px;
    --ai-widget-primary-color: ${primaryColor};
    --ai-widget-accent-color: ${accentColor};
    --ai-widget-icon-color: #FFFFFF; 
    --ai-widget-connecting-stroke-color: #FFFFFF;
    
    --ai-widget-primary-rgb: ${hexToRgb(primaryColor)};
    --ai-widget-accent-rgb: ${hexToRgb(accentColor)};
    
    --ai-widget-pulse-scale-before: 1;
    --ai-widget-pulse-opacity-before: 0;
    --ai-widget-pulse-scale-after: 1;
    --ai-widget-pulse-opacity-after: 0;
  }
  
  #vapi-sdk-button, .vapi-chat-button, #vapi-support-btn, 
  .ai-sdk-default-button { 
    display: none !important; 
  }

  .ai-widget-button-container {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: 20px 0;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  }
  .ai-widget-outer-container.ai-widget-floating {
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 9999;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  }

  #${widgetHtmlId} {
    width: var(--ai-widget-bubble-size);
    height: var(--ai-widget-bubble-size);
    background: linear-gradient(145deg, var(--ai-widget-primary-color), var(--ai-widget-primary-color));
    border-radius: 50%;
    box-shadow: 0 10px 30px rgba(0,0,0,0.15), 0 0 0 0 rgba(var(--ai-widget-accent-rgb),0);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform .25s cubic-bezier(.175,.885,.32,1.275), box-shadow .3s ease-out;
    outline: none;
    border: none;
    position: relative;
    -webkit-tap-highlight-color: transparent;
  }
  #${widgetHtmlId}:hover {
    transform: scale(1.08);
    box-shadow: 0 15px 40px rgba(var(--ai-widget-primary-rgb),.3);
  }
  #${widgetHtmlId}:active {
    transform: scale(1.02);
  }
  #${widgetHtmlId}::before,
  #${widgetHtmlId}::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background-color: transparent;
    border: 2px solid var(--ai-widget-accent-color);
    pointer-events: none;
    z-index: -1;
    transition: transform .4s cubic-bezier(0,0,.2,1), opacity .4s cubic-bezier(0,0,.2,1);
  }
  #${widgetHtmlId}::before {
    transform: translate(-50%,-50%) scale(var(--ai-widget-pulse-scale-before));
    opacity: var(--ai-widget-pulse-opacity-before);
  }
  #${widgetHtmlId}::after {
    transform: translate(-50%,-50%) scale(var(--ai-widget-pulse-scale-after));
    opacity: var(--ai-widget-pulse-opacity-after);
  }
  #${widgetHtmlId}.ai-widget-idle-animation {
    animation: ai-widget-breathing 4s infinite ease-in-out;
  }
  @keyframes ai-widget-breathing {
    0%,100% { transform:scale(1); }
    50% { transform:scale(1.03); }
  }
  #${widgetHtmlId}:hover.ai-widget-idle-animation {
      animation-play-state: paused;
  }

  .ai-widget-icon-wrapper {
    width: 45%; 
    height: 45%; 
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
  }
  .ai-widget-icon-wrapper .ai-widget-icon {
    position: absolute;
    width: 100%;
    height: 100%;
    opacity: 0;
    transform: scale(.8);
    transition: opacity .3s ease-in-out,transform .3s ease-in-out;
  }
  .ai-widget-icon-wrapper img.ai-widget-icon {
    border-radius: 50%; 
    object-fit: cover; 
  }
  .ai-widget-icon-wrapper svg.ai-widget-icon {
    fill: var(--ai-widget-icon-color);
  }
  .ai-widget-icon-wrapper .ai-widget-icon.visible {
    opacity: 1;
    transform: scale(1);
  }
  .ai-widget-icon-wrapper svg#${connectingIconId} .ai-widget-spinner {
    animation: ai-widget-spin 1s linear infinite;
    transform-origin: center;
    stroke: var(--ai-widget-connecting-stroke-color);
  }
  @keyframes ai-widget-spin {
    0% { transform:rotate(0deg); }
    100% { transform:rotate(360deg); }
  }

  #${widgetHtmlId}.ai-widget-call-active.ai-widget-listening {
    box-shadow: 0 0 25px 5px rgba(var(--ai-widget-primary-rgb),.35),
                0 0 0 3px rgba(var(--ai-widget-accent-rgb),.6);
  }
  #${widgetHtmlId}.ai-widget-call-active.ai-widget-listening .ai-widget-icon-wrapper #${listeningIconId} {
    animation: ai-widget-slow-glow-icon 2s infinite alternate ease-in-out;
  }
  @keyframes ai-widget-slow-glow-icon {
    from { filter:brightness(1); transform:scale(1); }
    to { filter:brightness(1.2) drop-shadow(0 0 3px var(--ai-widget-icon-color)); transform:scale(1.05); }
  }

  #${widgetHtmlId}.ai-widget-is-speaking::before {
      transition: transform 0.1s ease-out, opacity 0.1s ease-out;
  }
  #${widgetHtmlId}.ai-widget-is-speaking::after {
      transition: transform 0.1s ease-out 0.05s, opacity 0.1s ease-out 0.05s; 
  }

</style>
<script>
(function(){
  var aiAssistantSDKLoaded = false;
  var aiAssistantInstance = null;
  
  function loadAISDK(callback) {    
    if (document.querySelector('script[src*="client-sdk-html-script-tag"]')) {
      var checkInterval = setInterval(function() {        
        if (window.vapiSDK) { 
          clearInterval(checkInterval);
          aiAssistantSDKLoaded = true;
          callback();
        }
      }, 100);
      return;
    }
    var scriptElement = document.createElement('script');    
    scriptElement.src = 'https://cdn.jsdelivr.net/gh/VapiAI/client-sdk-html-script-tag@latest/dist/assets/index.js';
    scriptElement.defer = true;
    scriptElement.async = true;
    scriptElement.onload = function() {
      aiAssistantSDKLoaded = true;
      callback();
    };
    scriptElement.onerror = function() {
      console.error('AI Widget: Core AI Assistant SDK failed to load. Please check network or script URL.');
    };
    var firstScriptTag = document.getElementsByTagName('script')[0];
    if (firstScriptTag && firstScriptTag.parentNode) {
        firstScriptTag.parentNode.insertBefore(scriptElement, firstScriptTag);
    } else {        
        document.head.appendChild(scriptElement);
    }
  }
  
  function initializeAIWidget() {
    if (!aiAssistantSDKLoaded || !window.vapiSDK) { 
      console.error('AI Widget: AI Assistant SDK not available or not loaded properly.');
      return;
    }

    var callButton = document.getElementById('${widgetHtmlId}');
    var mainIconElement = document.getElementById('${mainIconId}');
    var connectingIconElement = document.getElementById('${connectingIconId}');
    var listeningIconElement = document.getElementById('${listeningIconId}');
    var assistantToCall = '${safeAssistantId}';
    var apiKey = '${serviceapiKey}';         
    const AISDK = window.vapiSDK;

    try {      
      aiAssistantInstance = AISDK.run({
        apiKey: apiKey,
        assistant: assistantToCall
      });
    } catch (e) {
      console.error("AI Widget: Error initializing AI Assistant SDK:", e);
      if(callButton) callButton.setAttribute('aria-label', 'AI Assistant Error');
      return;
    }

    function showIconState(iconElementToShow) {
      [mainIconElement, connectingIconElement, listeningIconElement].forEach(function(iconEl) {
        if (iconEl) iconEl.classList.remove('visible');
      });
      if (iconElementToShow) {
        iconElementToShow.classList.add('visible');
      }
    }

    function resetPulseAnimation() {
      if (callButton) {
        callButton.style.setProperty('--ai-widget-pulse-scale-before', '1');
        callButton.style.setProperty('--ai-widget-pulse-opacity-before', '0');
        callButton.style.setProperty('--ai-widget-pulse-scale-after', '1');
        callButton.style.setProperty('--ai-widget-pulse-opacity-after', '0');
      }
    }

    if (callButton) {
      showIconState(mainIconElement); 
      callButton.classList.add('ai-widget-idle-animation');
      resetPulseAnimation();

      callButton.addEventListener('click', function() {
        if (!aiAssistantInstance) {
          console.error('AI Widget: AI Assistant instance not available. Cannot start call.');
          return;
        }
        if (!callButton.classList.contains('ai-widget-call-active') && !callButton.classList.contains('ai-widget-connecting')) {
          callButton.classList.remove('ai-widget-idle-animation');
          callButton.classList.add('ai-widget-connecting');
          showIconState(connectingIconElement);
          callButton.disabled = true;
          
          const assistantOverrides = {
            metadata: {
              saas_call_type: "Web Call"
            }
          };

          aiAssistantInstance.start(assistantToCall, assistantOverrides)
            .catch(function(err) {
              console.error('AI Widget: Error starting AI call:', err);
              callButton.classList.remove('ai-widget-connecting');
              callButton.classList.add('ai-widget-idle-animation');
              showIconState(mainIconElement);
              callButton.disabled = false;
              callButton.setAttribute('aria-label', 'Error. Try AI Call Again.');
            });
        } else if (callButton.classList.contains('ai-widget-call-active')) {
          aiAssistantInstance.stop();
        }
      });
    } else {
      console.error('AI Widget: Call button element not found in the DOM.');
    }

    if (aiAssistantInstance) {      
      aiAssistantInstance.on('call-start', function() {        
        if (callButton) {
          callButton.classList.remove('ai-widget-connecting', 'ai-widget-idle-animation');
          callButton.classList.add('ai-widget-call-active', 'ai-widget-listening');
          callButton.disabled = false;
          showIconState(listeningIconElement);
          callButton.setAttribute('aria-label', 'End AI Call');
        }
      });

      aiAssistantInstance.on('call-end', function() {        
        if (callButton) {
          callButton.classList.remove('ai-widget-call-active', 'ai-widget-is-speaking', 'ai-widget-listening', 'ai-widget-connecting');
          callButton.classList.add('ai-widget-idle-animation');
          callButton.disabled = false;
          showIconState(mainIconElement);
          callButton.setAttribute('aria-label', 'Start AI Call');
          resetPulseAnimation();
        }
      });

      aiAssistantInstance.on('speech-start', function() {        
        if (callButton) {
          callButton.classList.add('ai-widget-is-speaking');
          callButton.classList.remove('ai-widget-listening');
        }
      });
      
      aiAssistantInstance.on('speech-end', function() {        
        if (callButton) {
          callButton.classList.remove('ai-widget-is-speaking');
          if (callButton.classList.contains('ai-widget-call-active')) {
            callButton.classList.add('ai-widget-listening');
          }
          resetPulseAnimation();
        }
      });

      aiAssistantInstance.on('volume-level', function(volume) {        
        if (callButton && callButton.classList.contains('ai-widget-is-speaking')) {
          var baseOpacity = 0.15; 
          var maxOpacity = 0.7;
          var baseScale = 1.0;    
          var maxScaleAddition = 0.6; 
          var SENSITIVITY_BOOST = 2.5; 
          var effectiveVolume = Math.min(1.0, volume * SENSITIVITY_BOOST);

          var scaleBefore = baseScale + (effectiveVolume * maxScaleAddition * 0.7); 
          var opacityBefore = Math.min(maxOpacity, baseOpacity + (effectiveVolume * (maxOpacity - baseOpacity) * 0.8));
          var scaleAfter = baseScale + (effectiveVolume * maxScaleAddition); 
          var opacityAfter = Math.min(maxOpacity, baseOpacity + (effectiveVolume * (maxOpacity - baseOpacity)));
          
          callButton.style.setProperty('--ai-widget-pulse-scale-before', scaleBefore.toString());
          callButton.style.setProperty('--ai-widget-pulse-opacity-before', opacityBefore.toString());
          callButton.style.setProperty('--ai-widget-pulse-scale-after', scaleAfter.toString());
          callButton.style.setProperty('--ai-widget-pulse-opacity-after', opacityAfter.toString());
        }
      });

      aiAssistantInstance.on('error', function(err) {
        console.error('AI Widget: AI Assistant SDK reported an error:', err);
        if (callButton) {
          callButton.classList.remove('ai-widget-call-active', 'ai-widget-is-speaking', 'ai-widget-listening', 'ai-widget-connecting');
          callButton.classList.add('ai-widget-idle-animation');
          callButton.disabled = false;
          showIconState(mainIconElement);
          callButton.setAttribute('aria-label', 'Error. Try AI Call Again.');
          resetPulseAnimation();
        }
      });
    }
  }  
  loadAISDK(initializeAIWidget);
})();
</script>`;
}